/**
 * Staff Manager
 * 
 * Provides focused staff management with consistent patterns and enhanced performance
 * Leverages the userManager for core user operations while adding staff-specific logic
 */

import type { User, UserRoleType, UserStatusType } from '@/lib/types';
import { STAFF_PAGE_ROLES } from '@/lib/constants';
import { userManager } from './userManager';
import { FilterBuilder } from '@/lib/utils/apiHelpers';

export interface StaffFilters {
  searchQuery?: string;
  role?: UserRoleType | 'All';
  status?: UserStatusType | 'All';
  organizationId?: string;
  clubId?: string;
}

type CreateStaffData = Omit<User, 'uid' | 'creationTime' | 'dateUpdated' | 'displayName'> & {
  password?: string;
  displayName?: string;
};

/**
 * Simplified staff manager with reduced complexity
 * Eliminates repetitive patterns and complex conditional logic
 */
class StaffManager {
  /**
   * Get all staff members (users with staff roles)
   */
  async getAllStaff(filters?: StaffFilters): Promise<User[]> {
    const allUsers = await userManager.getAllUsers();
    
    // Filter to only staff roles (exclude Super Admin for staff management)
    let staffUsers = new FilterBuilder(allUsers)
      .custom(user => {
        const userRoles = user.roles || [];
        return userRoles.some(role => STAFF_PAGE_ROLES.includes(role)) && 
               !userRoles.includes('Super Admin');
      })
      .build();

    // Apply additional filters if provided
    if (filters) {
      staffUsers = this.applyFilters(staffUsers, filters);
    }

    return staffUsers;
  }

  /**
   * Get staff member by ID
   */
  async getStaffById(uid: string): Promise<User | undefined> {
    const user = await userManager.getUserById(uid);
    
    if (!user) return undefined;
    
    // Verify user has staff role
    const userRoles = user.roles || [];
    const hasStaffRole = userRoles.some(role => STAFF_PAGE_ROLES.includes(role));
    
    return hasStaffRole ? user : undefined;
  }

  /**
   * Get staff members by role
   */
  async getStaffByRole(role: UserRoleType): Promise<User[]> {
    if (!STAFF_PAGE_ROLES.includes(role)) {
      return [];
    }
    
    return await userManager.getUsersByRole(role);
  }

  /**
   * Create staff member
   */
  async createStaff(staffData: CreateStaffData): Promise<User> {
    // Ensure staff roles are assigned
    const roles = staffData.roles || ['Staff'];
    const validStaffRoles = roles.filter(role => STAFF_PAGE_ROLES.includes(role));
    
    if (validStaffRoles.length === 0) {
      throw new Error('At least one valid staff role must be assigned');
    }

    const userData = {
      ...staffData,
      roles: validStaffRoles,
    };

    const newUser = await userManager.createUser(userData);
    if (!newUser) {
      throw new Error('Failed to create user');
    }
    return newUser;
  }

  /**
   * Update staff member
   */
  async updateStaff(uid: string, updates: Partial<User>): Promise<User | null> {
    // Verify user exists and has staff role
    const existingStaff = await this.getStaffById(uid);
    if (!existingStaff) {
      throw new Error('Staff member not found');
    }

    // If roles are being updated, ensure they're valid staff roles
    if (updates.roles) {
      const validStaffRoles = updates.roles.filter(role => STAFF_PAGE_ROLES.includes(role));
      if (validStaffRoles.length === 0) {
        throw new Error('At least one valid staff role must be assigned');
      }
      updates.roles = validStaffRoles;
    }

    return await userManager.updateUser(uid, updates);
  }

  /**
   * Delete staff member
   */
  async deleteStaff(uid: string): Promise<boolean> {
    // Verify user exists and has staff role
    const existingStaff = await this.getStaffById(uid);
    if (!existingStaff) {
      return false;
    }

    return await userManager.deleteUser(uid);
  }

  /**
   * Search staff with flexible criteria
   */
  async searchStaff(criteria: StaffFilters): Promise<User[]> {
    // Use the simple searchUsers method with just the search term
    const users = await userManager.searchUsers(criteria.searchQuery || '');
    
    // Apply all filters including staff role filtering
    return new FilterBuilder(users)
      .custom(user => {
        const userRoles = user.roles || [];
        // Must have staff role and not be Super Admin
        const hasStaffRole = userRoles.some(role => STAFF_PAGE_ROLES.includes(role)) && 
               !userRoles.includes('Super Admin');
        if (!hasStaffRole) return false;
        
        // Apply role filter if specified
        if (criteria.role && criteria.role !== 'All') {
          if (!userRoles.includes(criteria.role)) return false;
        }
        
        return true;
      })
      .where('status', criteria.status === 'All' ? null : criteria.status as UserStatusType | null)
      .where('organizationId', criteria.organizationId)
      .custom(user => !criteria.clubId || 
        (user.associatedClubIds?.includes(criteria.clubId) ?? false))
      .build();
  }

  /**
   * Get staff statistics
   */
  async getStaffStats(): Promise<{
    total: number;
    active: number;
    suspended: number;
    byRole: Record<string, number>;
    byOrganization: Record<string, number>;
  }> {
    const staff = await this.getAllStaff();
    
    const stats = {
      total: staff.length,
      active: staff.filter(s => s.status === 'Active').length,
      suspended: staff.filter(s => s.status === 'Suspended').length,
      byRole: {} as Record<string, number>,
      byOrganization: {} as Record<string, number>
    };

    // Group by role
    staff.forEach(member => {
      const roles = member.roles || [];
      roles.forEach(role => {
        if (STAFF_PAGE_ROLES.includes(role)) {
          stats.byRole[role] = (stats.byRole[role] || 0) + 1;
        }
      });
    });

    // Group by organization
    staff.forEach(member => {
      const orgId = member.organizationId || 'unassigned';
      stats.byOrganization[orgId] = (stats.byOrganization[orgId] || 0) + 1;
    });

    return stats;
  }

  /**
   * Get staff by organization
   */
  async getStaffByOrganization(organizationId: string): Promise<User[]> {
    return this.getAllStaff({ organizationId });
  }

  /**
   * Get staff by club
   */
  async getStaffByClub(clubId: string): Promise<User[]> {
    return this.getAllStaff({ clubId });
  }

  /**
   * Validate staff data
   */
  validateStaffData(data: CreateStaffData | Partial<User>): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Required field validation for new staff
    if ('email' in data && !data.email?.trim()) {
      errors.push('Email is required');
    }

    if ('firstName' in data && !data.firstName?.trim()) {
      errors.push('First name is required');
    }

    if ('lastName' in data && !data.lastName?.trim()) {
      errors.push('Last name is required');
    }

    // Role validation
    if (data.roles) {
      const validStaffRoles = data.roles.filter(role => STAFF_PAGE_ROLES.includes(role));
      if (validStaffRoles.length === 0) {
        errors.push('At least one valid staff role must be assigned');
      }
    }

    // Email format validation
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.push('Invalid email format');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Check if user has staff privileges
   */
  async hasStaffRole(uid: string): Promise<boolean> {
    const user = await userManager.getUserById(uid);
    if (!user) return false;
    
    const userRoles = user.roles || [];
    return userRoles.some(role => STAFF_PAGE_ROLES.includes(role));
  }

  /**
   * Apply filters to staff list
   */
  private applyFilters(staff: User[], filters: StaffFilters): User[] {
    return new FilterBuilder(staff)
      .search(filters.searchQuery, ['displayName', 'email', 'firstName', 'lastName'])
      .custom(user => !filters.role || filters.role === 'All' || 
        (user.roles?.includes(filters.role) ?? false))
      .where('status', filters.status === 'All' ? null : filters.status as UserStatusType | null)
      .where('organizationId', filters.organizationId)
      .custom(user => !filters.clubId || 
        (user.associatedClubIds?.includes(filters.clubId) ?? false))
      .build();
  }
}

// Export singleton instance
export const staffManager = new StaffManager();
export { StaffManager }; 
